import React from 'react';
import { css } from '@emotion/react';
import { Icon } from '@frontend/icons';
import { breakpoints } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import { Chip, ChipVariants, CategoryChipVariants } from '@frontend/design-system';
import { CallIntelChipVariant } from '../../hooks/use-call-intel-defaults';

type ChipType = 'chip' | 'category' | 'serviceQualityFlag' | 'schedulingOutcome';

type StrikeThroughConfig = {
  color?: string;
  thickness?: number;
  opacity?: number;
};

type CallIntelChipProps<T extends ChipType = 'chip', P extends ChipVariants | CategoryChipVariants = ChipVariants> = {
  as?: T;
  chipVariants: CallIntelChipVariant<P>;
  dataKey: string;
  label: string;
  strikeThrough?: boolean;
  strikeThroughConfig?: StrikeThroughConfig;
} & (T extends 'category'
  ? Omit<React.ComponentProps<typeof Chip.Category>, 'label'>
  : Omit<React.ComponentProps<typeof Chip>, 'children'>);

export const CallIntelChip = <
  T extends ChipType = 'chip',
  P extends ChipVariants | CategoryChipVariants = ChipVariants
>({
  as = 'chip' as T,
  chipVariants,
  dataKey,
  label,
  strikeThrough = false,
  strikeThroughConfig,
  hideTooltip = false,
  ...props
}: CallIntelChipProps<T, P>) => {
  const {
    variant,
    variantColor,
    leftIconName,
    fullWidth,
    textColor,
    leftIconColor,
    backgroundColor,
    border,
    borderStyle,
  } = chipVariants[dataKey] || {};

  if (as === 'category') {
    return (
      <Chip.Category indicatorColor={variantColor} variant={variant as CategoryChipVariants}>
        {label}
      </Chip.Category>
    );
  }

  return (
    <div css={strikeThrough && styles.strikeThrough(strikeThroughConfig)}>
      <Chip
        variant={variant as ChipVariants}
        {...(leftIconName && { leftElement: <Icon name={leftIconName} /> })}
        {...(props as React.ComponentProps<typeof Chip>)}
        css={[
          !label && styles.noGap,
          fullWidth && styles.fullWidth,
          textColor && styles.textColor(textColor),
          leftIconColor && styles.leftIconColor(leftIconColor),
          backgroundColor && styles.backgroundColor(backgroundColor),
          border && styles.border(border),
          borderStyle && styles.borderStyle(borderStyle),
          styles.customCss(as),
        ]}
        hideTooltip={hideTooltip}
      >
        {label}
      </Chip>
    </div>
  );
};

const styles = {
  noGap: css`
    gap: ${theme.spacing(0)};
  `,
  fullWidth: css`
    max-width: fit-content;
  `,

  textColor: (color: string) => css`
    color: ${color};
  `,

  leftIconColor: (color: string) => css`
    svg {
      color: ${color};
    }
  `,

  backgroundColor: (color: string) => css`
    background-color: ${color};
  `,
  border: (border: string) => css`
    border: ${border};
  `,
  borderStyle: (borderStyle: React.CSSProperties['borderStyle']) => css`
    border-style: ${borderStyle};
  `,

  strikeThrough: (config: StrikeThroughConfig = {}) => {
    const { color = theme.colors.text.subdued, thickness = 1, opacity = 0.5 } = config;

    return css`
      position: relative;
      opacity: ${opacity};

      /* draw a line over everything: icon + text */
      &::after {
        content: '';
        position: absolute;
        left: ${theme.spacing(1)};
        right: ${theme.spacing(1)};
        top: 50%;
        transform: translateY(-50%);
        border-top: ${thickness}px solid ${color};
      }
    `;
  },

  customCss: (as: ChipType) => {
    switch (as) {
      // This some extra modification to handle scheduling-outcome chip for small mode
      case 'schedulingOutcome':
        return css`
          @media screen and (max-width: ${breakpoints.small.max}px) {
            max-width: 100%;
          }
        `;
      default:
        return null;
    }
  },
};
